// Dishwasher.js - Máquina de loiça Bosch, via Home Connect
//
// É uma vista só de consulta: a Bosch não permite iniciar programas
// remotamente nesta máquina, e o rodapé explica-o para que a ausência de
// botões não pareça uma funcionalidade em falta.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Utensils, DoorOpen, Activity, AlertCircle } from 'lucide-react';
import { useHomeConnect, valorDe } from '../hooks/useHomeConnect';
import ViewHeader from './ViewHeader';

// A Bosch devolve estados como enums com caminho completo. O sufixo a seguir
// ao último ponto é o que interessa e é o que vira chave de tradução.
function chaveDoEstado(enumCompleto) {
  if (!enumCompleto) return null;
  return enumCompleto.split('.').pop();
}

function Dishwasher({ onBack }) {
  const { t } = useTranslation();
  const { appliances, loading, error, fetchAppliances } = useHomeConnect('Dishwasher');

  const maquina = appliances.find((a) => a.type === 'Dishwasher');
  const estado = valorDe(maquina, 'BSH.Common.Status.OperationState');
  const porta = valorDe(maquina, 'BSH.Common.Status.DoorState');
  const arranqueRemoto = valorDe(maquina, 'BSH.Common.Status.RemoteControlStartAllowed');

  // defaultValue faz com que um estado novo que a Bosch venha a inventar
  // apareça em bruto em vez de desaparecer do ecrã.
  const traduzir = (enumCompleto, alternativa) => {
    const chave = chaveDoEstado(enumCompleto);
    return chave ? t(`loica.estados.${chave}`, { defaultValue: chave }) : alternativa;
  };

  return (
    <div className="app-container">
      <div className="app-content">
        <ViewHeader
          icon={Utensils}
          iconClass="icon-heatpump"
          titulo={t('loica.titulo')}
          subtitulo={t('loica.subtitulo')}
          onBack={onBack}
          onRefresh={fetchAppliances}
          loading={loading}
        />

        {error && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {!maquina ? (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>{t('loica.aCarregar')}</p>
          </div>
        ) : (
          <div className="heatpump-container">
            <div className={`heatpump-status-card ${maquina.connected ? 'online' : 'offline'}`}>
              <div className="heatpump-status-header">
                <div className="status-indicator">
                  <div className={`status-dot ${maquina.connected ? 'online' : 'offline'}`} />
                  <span>{maquina.connected ? t('comum.online') : t('comum.offline')}</span>
                </div>
                <div className={`power-badge ${estado?.includes('Run') ? 'on' : 'off'}`}>
                  {traduzir(estado, t('loica.desconhecido'))}
                </div>
              </div>
            </div>

            <div className="heatpump-info-card">
              <h3>{t('comum.informacoes')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <DoorOpen size={20} />
                  <div>
                    <span className="info-label">{t('loica.porta')}</span>
                    <span className="info-value">{traduzir(porta, '—')}</span>
                  </div>
                </div>
                <div className="info-item">
                  <Activity size={20} />
                  <div>
                    <span className="info-label">{t('comum.estado')}</span>
                    <span className="info-value">{traduzir(estado, '—')}</span>
                  </div>
                </div>
              </div>
              <p className="section-help" style={{ marginTop: '1rem' }}>
                {arranqueRemoto
                  ? t('loica.arranqueRemotoPermitido')
                  : t('loica.arranqueRemotoBloqueado')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dishwasher;
